// src/pages/FamilyOfficeDashboard.jsx
import ValuationCaption from "../components/ValuationCaption";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { VISION_URL } from "../config/domains";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import PortfolioReadinessCard from "../components/PortfolioReadinessCard";
import FamilyOfficeReportButton from "../components/FamilyOfficeReportButton";
import SalePreparation from "@/components/familyoffice/SalePreparation";
import {
  exportConsolidatedCsv,
  generateConsolidatedPdf,
} from "../utils/FamilyOfficeConsolidatedExport";
import {
  Users,
  AlertCircle,
  Search,
  RefreshCw,
  Briefcase,
  PieChart,
  ShieldCheck,
  FileText,
  ChevronRight,
  HeartHandshake,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  FolderOpen,
  Target,
  Scale,
  ArrowRight,
  Info,
  Download,
Landmark,
Shield,
TrendingUp,
Heart,
  Building2,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const COPY = {
  fr: {
    loading: "Chargement de vos données...",
    familiesFollowed: "Familles suivies",
    totalValue: "Encours global",
    avgPerf: "Performance moyenne",
    vsAcq: "vs acquisition",
    riskLevel: "Niveau de risque",
    fSucc: "Succession structurée",
    fPatr: "Programme mécénat",
    allRisk: "Tous niveaux de risque",
    riskHigh: "Risque élevé",
    riskMed: "Risque moyen",
    riskLow: "Risque faible",
    searchClient: "Rechercher un client...",
    csvTitle: "Export CSV de toutes les œuvres, toutes familles (compatible ERP / IFI)",
    pdfTitle: "Rapport patrimonial consolidé (PDF)",
    todoTitle: "Points à traiter ce trimestre",
    t1: "Démarchage & succession",
    t1link: "Préparer un rapport →",
    t2: "Campagne de valorisation",
    t2txt: "Cibler les familles dont les valorisations datent de +24 mois.",
    t2link: "Lancer les valorisations →",
    t3: "Mécénat & philanthropie",
    t3txt: "Stratégies de mécénat et de don pour le client actif.",
    t3link: "Ouvrir le conseil philanthropie →",
    t4: "Opportunités de prêts",
    t4txt: "Identifier les œuvres prêtables pour expositions 2025–2026.",
    t4link: "Explorer les institutions →",
    t5: "Maisons de vente",
    t5txt: "Suivre les ventes pertinentes (comparables, acquisitions).",
    t5link: "Suivre les ventes →",
    visionBrand: "ARTICHAUT Vision IA",
    visionTitle: "Intelligence collection",
    visionOpen: "Ouvrir Vision IA ↗",
    aiAnalyses: "Analyses IA",
    consReports: "Rapports conservation",
    allUpToDate: "Tout à jour",
    aiValuations: "Valorisations IA",
    blockchain: "Blockchain",
    certsIssued: "certificats émis",
    topStyles: "Styles dominants (IA)",
    visionWarn1: "Moins de la moitié des oeuvres ont été analysées par Vision IA.",
    visionWarnLink: "Lancer les analyses",
    visionWarn2: "pour enrichir les données de vos familles clientes.",
    portfolio: "Portefeuille clients & familles",
    byValue: "Par encours",
    byRisk: "Par risque",
    badgeSucc: "Succession",
    badgeMec: "Mécénat",
    estValue: "Encours estimé",
    viewFamily: "Voir famille",
    managedArt: "Œuvres gérées",
    transmissionPlan: "Plan de transmission",
    managedTitle: "Voir toutes les œuvres gérées de ce client",
    transTitle: "Consulter et compléter le dossier de transmission de ce client",
    reportsTitle: "Rapports patrimoniaux",
    reportsIntro: "Générez un rapport PDF pour une famille — inventaire, conservation IA, héritiers, blockchain, données successorales.",
    reportsNote: "Le rapport est téléchargé depuis la fiche famille.",
    modulesTitle: "Modules experts & Actions",
    modulesSub: "3 chantiers prioritaires et modules spécialisés",
    m1: "Sécurisation juridique & fiscale",
    m2: "Assurance & gestion des risques",
    m2txt: "Polices, garanties et zones de vulnérabilité",
    m3: "Philanthropie & mécénat",
    m3txt: "Dons, prêts, fondations et défiscalisation",
    priorities: "Chantiers prioritaires",
    p1: "Prêts & visibilité institutionnelle",
    p2: "Opportunités maisons de vente",
    succMod: "Module Succession & Transmission",
    succModSub: "Simulations successorales & scénarios de répartition",
    succModTxt: "Pour chaque famille, ARTICHAUT agrège les valorisations, la structure de détention (pleine propriété, démembrement, sociétés), les préférences fiscales et les bénéficiaires pressentis.",
    scen: "Scénarios simulables",
    scenTxt: "Donation, démembrement, legs, réserve",
    params: "Paramètres pris en compte",
    paramsTxt: "Régime matrimonial, pays de résidence, nature des actifs",
    outFmt: "Format de sortie",
    outFmtTxt: "Rapport synthétique pour le client + annexes pour les avocats / notaires",
    taxPlan: "Planification fiscale détaillée",
    taxPlanTxt: "Optimisation des flux de donation / vente et simulations d'imposition par pays et par scénario.",
    inDev: "En cours de développement →",
    insMod: "Module Assurance & Risques",
    insModSub: "Suivi des polices, garanties et zones de vulnérabilité",
    insModTxt: "L'outil consolide les polices d'assurance, les garanties et les exclusions, en les confrontant aux estimations d'ARTICHAUT et à la réalité des mouvements d'œuvres.",
    cover: "Couverture globale",
    coverTxt: "Taux de couverture vs valorisation totale",
    exclus: "Analyse des exclusions",
    exclusTxt: "Conditions spécifiques, transport, prêt, événements",
    alerts: "Alertes",
    alertsTxt: "Renouvellements, sous-assurance, zones géographiques sensibles",
    insFull: "Module Assurance complet",
    insFullTxt: "Audit des polices, simulation d'ajustement de garanties et scénarios de sous/sur-assurance.",
    phMod: "Module Philanthropie & Mécénat",
    phModSub: "Dons, prêts, fondations, mécénat de compétences",
    phModTxt: "Suivi des engagements, des dons passés, des promesses de dons et des opportunités de mécénat cohérentes avec l'histoire de chaque famille.",
    phSeven: "🎨 7 Actions de Mécénat Culturel",
    phSevenTxt: "Accompagnez vos familles dans la valorisation et la transmission de leurs collections.",
    a1: "Prêts aux institutions",
    a1t: "Exposer temporairement des œuvres dans des musées prestigieux.",
    a1b: "💰 66% déduction • 3–36 mois",
    a2: "Expositions publiques",
    a2t: "Organiser des expositions temporaires de la collection.",
    a2b: "✨ Frais déductibles • Valorisation",
    a3: "Conservation & restauration",
    a3t: "Restaurer, documenter et numériser les œuvres de la collection.",
    a3b: "🛡️ Déduction partielle • Préservation",
    a4: "Location aux entreprises",
    a4t: "Louer des œuvres pour la décoration d'espaces professionnels.",
    a4b: "💵 Revenus défiscalisables • 1–5 ans",
    a5: "Donation avec usufruit",
    a5t: "Donner la propriété tout en conservant l'usage viager des œuvres.",
    a5b: "🎁 66% + 0% succession • Viager",
    a6: "Actions éducatives",
    a6t: "Visites scolaires, ateliers pédagogiques autour de la collection.",
    a6b: "📚 60% déduction • Impact social",
    a7: "Achat pour donation",
    a7t: "Acquérir des œuvres complémentaires pour ensuite les donner.",
    a7b: "🎯 66% de l'achat • Enrichissement",
    prio: "Priorisation accompagnement",
    prioTxt: "Générez une liste d'actions priorisées par famille, en fonction des risques, opportunités fiscales et contraintes calendaires.",
    quarterly: "Lancer une revue trimestrielle",
    mTitle: "Titre",
    mArtist: "Artiste",
    mValue: "Valeur estimée",
    mLoc: "Localisation",
    mNone: "Aucune œuvre trouvée pour ce client.",
    sellPrep: "Mise en vente",
    close: "Fermer",
    errTitle: "Erreur",
    repPick: "Sélectionnez la famille pour laquelle générer le rapport. Vous serez redirigé(e) vers sa fiche où le PDF peut être téléchargé.",
    noFam: "Aucune famille disponible.",
    cancel: "Annuler",
    generating: "Génération…",
    pdfReport: "Rapport PDF",
    noContact: "Contact non renseigné",
    untitled: "Sans titre",
    unknownArtist: "Artiste inconnu",
    familyFallback: "Famille",
    worksLower: "oeuvres",
    toRevisit: "à revisiter",
    conf: "conf.",
    noExport: "Aucune œuvre à exporter pour le moment.",
    csvError: "Erreur lors de l'export CSV. Veuillez réessayer.",
    pdfError: "Erreur lors de la génération du rapport. Veuillez réessayer.",
    repOverview: "Rapport patrimonial",
    repOverviewSub: "Vue complète de la collection",
    repSucc: "Plan de transmission",
    repSuccSub: "Héritiers, matching, données notaire",
    repPhil: "Stratégie de mécénat",
    repPhilSub: "Leviers fiscaux et engagements",
    nClients: (n) => `${n} client${n > 1 ? "s" : ""}`,
    nWorks: (n) => `${n} œuvres`,
    followedClients: (n) => `${n} client${n > 1 ? "s" : ""} suivi${n > 1 ? "s" : ""}`,
    nCollections: (n) => `${n} collection${n > 1 ? "s" : ""}`,
    nArtworks: (n) => `${n} œuvre${n > 1 ? "s" : ""}`,
    nMembers: (n) => `${n} membre${n > 1 ? "s" : ""}`,
    noPlan: (n) => `${n} client(s) sans plan structuré. Prioriser un cadrage avec notaire/avocat.`,
    withPlans: (n) => `${n} dossiers avec plans de succession structurés`,
    withFile: (n) => `${n} client${n > 1 ? "s" : ""} avec dossier structuré`,
    noFamily: (name) => `Aucune famille trouvée pour ${name}. Rattachez d'abord ce client à une famille (Gestion des familles).`,
  },
  en: {
    loading: "Loading your data...",
    familiesFollowed: "Families followed",
    totalValue: "Total value",
    avgPerf: "Average performance",
    vsAcq: "vs acquisition",
    riskLevel: "Risk level",
    fSucc: "Structured succession",
    fPatr: "Patronage programme",
    allRisk: "All risk levels",
    riskHigh: "High risk",
    riskMed: "Medium risk",
    riskLow: "Low risk",
    searchClient: "Search a client...",
    csvTitle: "CSV export of all artworks, all families (ERP / wealth-tax compatible)",
    pdfTitle: "Consolidated wealth report (PDF)",
    todoTitle: "Items to handle this quarter",
    t1: "Outreach & succession",
    t1link: "Prepare a report →",
    t2: "Valuation campaign",
    t2txt: "Target the families whose valuations are over 24 months old.",
    t2link: "Start the valuations →",
    t3: "Patronage & philanthropy",
    t3txt: "Patronage and giving strategies for the active client.",
    t3link: "Open philanthropy advice →",
    t4: "Loan opportunities",
    t4txt: "Identify the artworks available to lend for 2025–2026 exhibitions.",
    t4link: "Explore institutions →",
    t5: "Auction houses",
    t5txt: "Follow relevant sales (comparables, acquisitions).",
    t5link: "Follow sales →",
    visionBrand: "ARTICHAUT Vision AI",
    visionTitle: "Collection intelligence",
    visionOpen: "Open Vision AI ↗",
    aiAnalyses: "AI analyses",
    consReports: "Conservation reports",
    allUpToDate: "All up to date",
    aiValuations: "AI valuations",
    blockchain: "Blockchain",
    certsIssued: "certificates issued",
    topStyles: "Dominant styles (AI)",
    visionWarn1: "Fewer than half of the artworks have been analysed by Vision AI.",
    visionWarnLink: "Start the analyses",
    visionWarn2: "to enrich the data of your client families.",
    portfolio: "Client & family portfolio",
    byValue: "By value",
    byRisk: "By risk",
    badgeSucc: "Succession",
    badgeMec: "Patronage",
    estValue: "Estimated value",
    viewFamily: "View family",
    managedArt: "Managed artworks",
    transmissionPlan: "Transmission plan",
    managedTitle: "See all the artworks managed for this client",
    transTitle: "Read and complete this client's transmission file",
    reportsTitle: "Wealth reports",
    reportsIntro: "Generate a PDF report for a family — inventory, AI conservation, heirs, blockchain, succession data.",
    reportsNote: "The report is downloaded from the family sheet.",
    modulesTitle: "Expert modules & Actions",
    modulesSub: "3 priority workstreams and specialised modules",
    m1: "Legal & tax securing",
    m2: "Insurance & risk management",
    m2txt: "Policies, guarantees and vulnerability areas",
    m3: "Philanthropy & patronage",
    m3txt: "Donations, loans, foundations and tax relief",
    priorities: "Priority workstreams",
    p1: "Loans & institutional visibility",
    p2: "Auction-house opportunities",
    succMod: "Succession & Transmission module",
    succModSub: "Succession simulations & distribution scenarios",
    succModTxt: "For each family, ARTICHAUT aggregates the valuations, the holding structure (full ownership, split ownership, companies), the tax preferences and the prospective beneficiaries.",
    scen: "Simulable scenarios",
    scenTxt: "Donation, split ownership, bequests, reserved share",
    params: "Parameters taken into account",
    paramsTxt: "Marital regime, country of residence, type of assets",
    outFmt: "Output format",
    outFmtTxt: "Summary report for the client + annexes for lawyers / notaries",
    taxPlan: "Detailed tax planning",
    taxPlanTxt: "Optimisation of donation / sale flows and tax simulations by country and scenario.",
    inDev: "Under development →",
    insMod: "Insurance & Risks module",
    insModSub: "Tracking of policies, guarantees and vulnerability areas",
    insModTxt: "The tool consolidates insurance policies, guarantees and exclusions, comparing them with ARTICHAUT's estimates and the reality of artwork movements.",
    cover: "Overall coverage",
    coverTxt: "Coverage rate vs total valuation",
    exclus: "Exclusions analysis",
    exclusTxt: "Specific conditions, transport, loans, events",
    alerts: "Alerts",
    alertsTxt: "Renewals, under-insurance, sensitive geographic areas",
    insFull: "Full Insurance module",
    insFullTxt: "Policy audit, simulation of guarantee adjustments and under/over-insurance scenarios.",
    phMod: "Philanthropy & Patronage module",
    phModSub: "Donations, loans, foundations, skills-based patronage",
    phModTxt: "Tracking of commitments, past donations, pledges and patronage opportunities consistent with each family's history.",
    phSeven: "🎨 7 Cultural Patronage Actions",
    phSevenTxt: "Support your families in enhancing and transmitting their collections.",
    a1: "Loans to institutions",
    a1t: "Temporarily exhibit artworks in prestigious museums.",
    a1b: "💰 66% deduction • 3–36 months",
    a2: "Public exhibitions",
    a2t: "Organise temporary exhibitions of the collection.",
    a2b: "✨ Deductible costs • Enhancement",
    a3: "Conservation & restoration",
    a3t: "Restore, document and digitise the artworks of the collection.",
    a3b: "🛡️ Partial deduction • Preservation",
    a4: "Rental to companies",
    a4t: "Rent out artworks to decorate professional spaces.",
    a4b: "💵 Tax-relievable income • 1–5 years",
    a5: "Donation with usufruct",
    a5t: "Give the ownership while keeping lifetime use of the artworks.",
    a5b: "🎁 66% + 0% inheritance • Lifetime",
    a6: "Educational actions",
    a6t: "School visits and educational workshops around the collection.",
    a6b: "📚 60% deduction • Social impact",
    a7: "Purchase for donation",
    a7t: "Acquire complementary artworks in order to donate them.",
    a7b: "🎯 66% of the purchase • Enrichment",
    prio: "Support prioritisation",
    prioTxt: "Generate a list of actions prioritised by family, based on risks, tax opportunities and calendar constraints.",
    quarterly: "Start a quarterly review",
    mTitle: "Title",
    mArtist: "Artist",
    mValue: "Estimated value",
    mLoc: "Location",
    mNone: "No artwork found for this client.",
    sellPrep: "Prepare for sale",
    close: "Close",
    errTitle: "Error",
    repPick: "Select the family for which to generate the report. You will be taken to its sheet, where the PDF can be downloaded.",
    noFam: "No family available.",
    cancel: "Cancel",
    generating: "Generating…",
    pdfReport: "PDF report",
    noContact: "Contact not provided",
    untitled: "Untitled",
    unknownArtist: "Unknown artist",
    familyFallback: "Family",
    worksLower: "artworks",
    toRevisit: "to revisit",
    conf: "conf.",
    noExport: "No artwork to export at the moment.",
    csvError: "Error during the CSV export. Please try again.",
    pdfError: "Error while generating the report. Please try again.",
    repOverview: "Wealth report",
    repOverviewSub: "Full view of the collection",
    repSucc: "Transmission plan",
    repSuccSub: "Heirs, matching, notary data",
    repPhil: "Patronage strategy",
    repPhilSub: "Tax levers and commitments",
    nClients: (n) => `${n} client${n === 1 ? "" : "s"}`,
    nWorks: (n) => `${n} artworks`,
    followedClients: (n) => `${n} client${n === 1 ? "" : "s"} followed`,
    nCollections: (n) => `${n} collection${n === 1 ? "" : "s"}`,
    nArtworks: (n) => `${n} artwork${n === 1 ? "" : "s"}`,
    nMembers: (n) => `${n} member${n === 1 ? "" : "s"}`,
    noPlan: (n) => `${n} client(s) without a structured plan. Prioritise a scoping session with a notary/lawyer.`,
    withPlans: (n) => `${n} files with structured succession plans`,
    withFile: (n) => `${n} client${n === 1 ? "" : "s"} with a structured file`,
    noFamily: (name) => `No family found for ${name}. First attach this client to a family (Family management).`,
  },
  de: {
    loading: "Ihre Daten werden geladen...",
    familiesFollowed: "Betreute Familien",
    totalValue: "Gesamtwert",
    avgPerf: "Durchschnittliche Entwicklung",
    vsAcq: "ggü. Anschaffung",
    riskLevel: "Risikostufe",
    fSucc: "Strukturierte Nachfolge",
    fPatr: "Mäzenatentum-Programm",
    allRisk: "Alle Risikostufen",
    riskHigh: "Hohes Risiko",
    riskMed: "Mittleres Risiko",
    riskLow: "Niedriges Risiko",
    searchClient: "Kunden suchen...",
    csvTitle: "CSV-Export aller Werke, aller Familien (ERP-/Vermögensteuer-kompatibel)",
    pdfTitle: "Konsolidierter Vermögensbericht (PDF)",
    todoTitle: "Aufgaben in diesem Quartal",
    t1: "Ansprache & Nachfolge",
    t1link: "Bericht vorbereiten →",
    t2: "Bewertungskampagne",
    t2txt: "Familien ansprechen, deren Bewertungen älter als 24 Monate sind.",
    t2link: "Bewertungen starten →",
    t3: "Mäzenatentum & Philanthropie",
    t3txt: "Mäzenatentums- und Schenkungsstrategien für den aktiven Kunden.",
    t3link: "Philanthropie-Beratung öffnen →",
    t4: "Leihgabe-Chancen",
    t4txt: "Werke ermitteln, die für Ausstellungen 2025–2026 verleihbar sind.",
    t4link: "Institutionen erkunden →",
    t5: "Auktionshäuser",
    t5txt: "Relevante Verkäufe verfolgen (Vergleichswerte, Erwerbungen).",
    t5link: "Verkäufe verfolgen →",
    visionBrand: "ARTICHAUT Vision KI",
    visionTitle: "Sammlungsintelligenz",
    visionOpen: "Vision KI öffnen ↗",
    aiAnalyses: "KI-Analysen",
    consReports: "Zustandsberichte",
    allUpToDate: "Alles aktuell",
    aiValuations: "KI-Bewertungen",
    blockchain: "Blockchain",
    certsIssued: "ausgestellte Zertifikate",
    topStyles: "Dominante Stile (KI)",
    visionWarn1: "Weniger als die Hälfte der Werke wurde von Vision KI analysiert.",
    visionWarnLink: "Analysen starten",
    visionWarn2: "um die Daten Ihrer Kundenfamilien anzureichern.",
    portfolio: "Kunden- & Familienportfolio",
    byValue: "Nach Wert",
    byRisk: "Nach Risiko",
    badgeSucc: "Nachfolge",
    badgeMec: "Mäzenatentum",
    estValue: "Geschätzter Wert",
    viewFamily: "Familie ansehen",
    managedArt: "Verwaltete Werke",
    transmissionPlan: "Übertragungsplan",
    managedTitle: "Alle für diesen Kunden verwalteten Werke ansehen",
    transTitle: "Die Übertragungsakte dieses Kunden einsehen und ergänzen",
    reportsTitle: "Vermögensberichte",
    reportsIntro: "Erstellen Sie einen PDF-Bericht für eine Familie — Inventar, KI-Zustand, Erben, Blockchain, Nachfolgedaten.",
    reportsNote: "Der Bericht wird vom Familienblatt aus heruntergeladen.",
    modulesTitle: "Experten-Module & Aktionen",
    modulesSub: "3 prioritäre Vorhaben und Spezialmodule",
    m1: "Rechtliche & steuerliche Absicherung",
    m2: "Versicherung & Risikomanagement",
    m2txt: "Policen, Garantien und Schwachstellen",
    m3: "Philanthropie & Mäzenatentum",
    m3txt: "Schenkungen, Leihgaben, Stiftungen und Steuerentlastung",
    priorities: "Prioritäre Vorhaben",
    p1: "Leihgaben & institutionelle Sichtbarkeit",
    p2: "Chancen bei Auktionshäusern",
    succMod: "Modul Nachfolge & Übertragung",
    succModSub: "Nachfolgesimulationen & Verteilungsszenarien",
    succModTxt: "Für jede Familie bündelt ARTICHAUT die Bewertungen, die Besitzstruktur (Volleigentum, Nießbrauch/Nacktes Eigentum, Gesellschaften), die steuerlichen Präferenzen und die voraussichtlichen Begünstigten.",
    scen: "Simulierbare Szenarien",
    scenTxt: "Schenkung, Nießbrauch, Vermächtnisse, Pflichtteil",
    params: "Berücksichtigte Parameter",
    paramsTxt: "Güterstand, Wohnsitzland, Art der Vermögenswerte",
    outFmt: "Ausgabeformat",
    outFmtTxt: "Zusammenfassender Bericht für den Kunden + Anlagen für Anwälte / Notare",
    taxPlan: "Detaillierte Steuerplanung",
    taxPlanTxt: "Optimierung der Schenkungs- / Verkaufsflüsse und Steuersimulationen nach Land und Szenario.",
    inDev: "In Entwicklung →",
    insMod: "Modul Versicherung & Risiken",
    insModSub: "Verfolgung von Policen, Garantien und Schwachstellen",
    insModTxt: "Das Tool konsolidiert Versicherungspolicen, Garantien und Ausschlüsse und gleicht sie mit den Schätzungen von ARTICHAUT und den tatsächlichen Werkbewegungen ab.",
    cover: "Gesamtdeckung",
    coverTxt: "Deckungsquote ggü. Gesamtbewertung",
    exclus: "Analyse der Ausschlüsse",
    exclusTxt: "Besondere Bedingungen, Transport, Leihgaben, Veranstaltungen",
    alerts: "Warnungen",
    alertsTxt: "Verlängerungen, Unterversicherung, sensible geografische Zonen",
    insFull: "Vollständiges Versicherungsmodul",
    insFullTxt: "Policen-Audit, Simulation von Garantieanpassungen und Szenarien für Unter-/Überversicherung.",
    phMod: "Modul Philanthropie & Mäzenatentum",
    phModSub: "Schenkungen, Leihgaben, Stiftungen, Skill-basiertes Mäzenatentum",
    phModTxt: "Verfolgung von Verpflichtungen, früheren Schenkungen, Zusagen und Mäzenatentum-Chancen, die zur Geschichte jeder Familie passen.",
    phSeven: "🎨 7 Aktionen des Kulturmäzenatentums",
    phSevenTxt: "Begleiten Sie Ihre Familien bei der Aufwertung und Übertragung ihrer Sammlungen.",
    a1: "Leihgaben an Institutionen",
    a1t: "Werke vorübergehend in renommierten Museen ausstellen.",
    a1b: "💰 66 % Abzug • 3–36 Monate",
    a2: "Öffentliche Ausstellungen",
    a2t: "Temporäre Ausstellungen der Sammlung organisieren.",
    a2b: "✨ Abzugsfähige Kosten • Aufwertung",
    a3: "Konservierung & Restaurierung",
    a3t: "Die Werke der Sammlung restaurieren, dokumentieren und digitalisieren.",
    a3b: "🛡️ Teilabzug • Erhaltung",
    a4: "Vermietung an Unternehmen",
    a4t: "Werke zur Gestaltung von Geschäftsräumen vermieten.",
    a4b: "💵 Steuerlich entlastbare Einnahmen • 1–5 Jahre",
    a5: "Schenkung mit Nießbrauch",
    a5t: "Das Eigentum übertragen und die lebenslange Nutzung der Werke behalten.",
    a5b: "🎁 66 % + 0 % Erbschaft • Lebenslang",
    a6: "Bildungsaktionen",
    a6t: "Schulbesuche und pädagogische Workshops rund um die Sammlung.",
    a6b: "📚 60 % Abzug • Soziale Wirkung",
    a7: "Kauf zur Schenkung",
    a7t: "Ergänzende Werke erwerben, um sie anschließend zu schenken.",
    a7b: "🎯 66 % des Kaufpreises • Bereicherung",
    prio: "Priorisierung der Begleitung",
    prioTxt: "Erstellen Sie eine nach Familie priorisierte Aktionsliste, basierend auf Risiken, steuerlichen Chancen und Terminzwängen.",
    quarterly: "Quartalsüberprüfung starten",
    mTitle: "Titel",
    mArtist: "Künstler",
    mValue: "Geschätzter Wert",
    mLoc: "Standort",
    mNone: "Kein Werk für diesen Kunden gefunden.",
    sellPrep: "Verkauf vorbereiten",
    close: "Schließen",
    errTitle: "Fehler",
    repPick: "Wählen Sie die Familie, für die der Bericht erstellt werden soll. Sie werden zu ihrem Blatt weitergeleitet, wo das PDF heruntergeladen werden kann.",
    noFam: "Keine Familie verfügbar.",
    cancel: "Abbrechen",
    generating: "Wird erstellt…",
    pdfReport: "PDF-Bericht",
    noContact: "Kontakt nicht angegeben",
    untitled: "Ohne Titel",
    unknownArtist: "Unbekannter Künstler",
    familyFallback: "Familie",
    worksLower: "Werke",
    toRevisit: "zu überprüfen",
    conf: "Konf.",
    noExport: "Derzeit kein Werk zum Exportieren.",
    csvError: "Fehler beim CSV-Export. Bitte versuchen Sie es erneut.",
    pdfError: "Fehler beim Erstellen des Berichts. Bitte versuchen Sie es erneut.",
    repOverview: "Vermögensbericht",
    repOverviewSub: "Vollständige Sicht auf die Sammlung",
    repSucc: "Übertragungsplan",
    repSuccSub: "Erben, Matching, Notardaten",
    repPhil: "Mäzenatentum-Strategie",
    repPhilSub: "Steuerliche Hebel und Verpflichtungen",
    nClients: (n) => `${n} ${n === 1 ? "Kunde" : "Kunden"}`,
    nWorks: (n) => `${n} Werke`,
    followedClients: (n) => `${n} betreute${n === 1 ? "r Kunde" : " Kunden"}`,
    nCollections: (n) => `${n} ${n === 1 ? "Sammlung" : "Sammlungen"}`,
    nArtworks: (n) => `${n} ${n === 1 ? "Werk" : "Werke"}`,
    nMembers: (n) => `${n} ${n === 1 ? "Mitglied" : "Mitglieder"}`,
    noPlan: (n) => `${n} Kunde(n) ohne strukturierten Plan. Eine Klärung mit Notar/Anwalt priorisieren.`,
    withPlans: (n) => `${n} Akten mit strukturierten Nachfolgeplänen`,
    withFile: (n) => `${n} ${n === 1 ? "Kunde" : "Kunden"} mit strukturierter Akte`,
    noFamily: (name) => `Keine Familie für ${name} gefunden. Ordnen Sie diesen Kunden zuerst einer Familie zu (Familienverwaltung).`,
  },
};

export default function FamilyOfficeDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lg = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  const c = COPY[lg] || COPY.fr;
  const locale = lg === "de" ? "de-DE" : lg === "en" ? "en-GB" : "fr-FR";
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("value");
  const [activeModule, setActiveModule] = useState("overview");
  const [filterRisk, setFilterRisk] = useState("all");
  const [onlySuccession, setOnlySuccession] = useState(false);
  const [onlyPhilanthropy, setOnlyPhilanthropy] = useState(false);

  const [families, setFamilies] = useState([]);
  const [clientFamilyMap, setClientFamilyMap] = useState({});

  const [, setSuccessionPlans] = useState([]);
  const [, setPhilanthropy] = useState([]);

  const [showArtworksModal, setShowArtworksModal] = useState(false);
  const [modalArtworks, setModalArtworks] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSalePrep, setShowSalePrep] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportModalType, setReportModalType] = useState("overview");

const openSalePreparation = (artwork) => {
  setSelectedArtwork(artwork);
  setShowSalePrep(true);
};

  // Charger les familles avec le nombre de clients
  const loadFamilies = async () => {
    if (!user?.id) {
      console.error("❌ Pas d'utilisateur pour charger les familles");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("families")
        .select(`
          id,
          label,
          client_profile_id,
          family_office_id,
          status,
          notes,
          created_at,
          updated_at
        `)
        .eq("family_office_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("❌ Erreur chargement familles:", error);
        return;
      }

      if (data && data.length > 0) {
        const familiesWithClientCount = await Promise.all(
          data.map(async (f) => {
            const { data: familyClientsData, error: clientsError } = await supabase
              .from("family_clients")
              .select("id, client_id")
              .eq("family_id", f.id);

            if (clientsError) {
              console.error(`❌ Erreur comptage clients famille ${f.id}:`, clientsError);
            }

            const clientCount = familyClientsData?.length || 0;

            let client_name = null;
            if (f.client_profile_id) {
              try {
                const { data: profileData, error: profileErr } = await supabase
                  .from("profiles")
                  .select("first_name, last_name")
                  .eq("id", f.client_profile_id)
                  .single();

                if (!profileErr && profileData) {
                  client_name = `${profileData.first_name || ""} ${
                    profileData.last_name || ""
                  }`.trim();
                }
              } catch (err) {
                console.error(`❌ Erreur profil client pour famille ${f.id}:`, err);
              }
            }

            return {
              ...f,
              clientCount,
              client_name,
            };
          })
        );

        setFamilies(familiesWithClientCount);
        return familiesWithClientCount;
      } else {
        setFamilies([]);
        return [];
      }
    } catch (err) {
      console.error("❌ Erreur loadFamilies:", err);
      setFamilies([]);
      return [];
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reloadData = useCallback(async () => {
    if (!user?.id) {
      console.error("❌ Pas d'utilisateur connecté");
      return;
    }

    setLoading(true);
    try {
      // 1) Familles du FO
      const fams = await loadFamilies();
      const famIds = (fams || []).map((f) => f.id);

      // 2) Map client -> famille
      let map = {};
      if (famIds.length > 0) {
        const { data: links, error: linksErr } = await supabase
          .from("family_clients")
          .select("client_id, family_id")
          .in("family_id", famIds);

        if (linksErr) {
          console.error("❌ Erreur chargement family_clients:", linksErr);
        } else {
          (links || []).forEach((l) => {
            if (!l.client_id || !l.family_id) return;
            map[l.client_id] = l.family_id;
          });
        }
      }

      setClientFamilyMap(map);

      // 3) Permissions collections
      const { data: permissions, error: permError } = await supabase
        .from("collection_permissions")
        .select("collection_id, can_view, can_edit, client_id")
        .eq("grantee_id", user.id)
        .eq("can_view", true);

      if (permError) {
        console.error("❌ Erreur permissions:", permError);
        setLoading(false);
        return;
      }

      if (!permissions || permissions.length === 0) {
        setClients([]);
        setCollections([]);
        setArtworks([]);
        setLoading(false);
        return;
      }

      const clientIds = [
        ...new Set(permissions.map((p) => p.client_id).filter((id) => id != null)),
      ];

      if (clientIds.length === 0) {
        console.error("❌ Aucun client_id dans les permissions");
        setLoading(false);
        return;
      }

      let profiles = [];
      let profilesError = null;
      if (clientIds.length > 0) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .in("id", clientIds);
        profilesError = error;
        profiles = data || [];
      }

      if (profilesError) {
        console.error("❌ Erreur chargement profils:", profilesError);
        setLoading(false);
        return;
      }

      const collectionIds = [
        ...new Set(permissions.map((p) => p.collection_id).filter((id) => id != null)),
      ];

      let collections = [];
      if (collectionIds.length > 0) {
        const { data, error } = await supabase
          .from("collections")
          .select("*")
          .in("id", collectionIds);
        if (error) {
          console.error("❌ Erreur chargement collections:", error);
        } else {
          collections = data || [];
        }
      }

      const artworkCollectionIds = collections.map((c) => c.id);
      let artworks = [];
      if (artworkCollectionIds.length > 0) {
        const { data } = await supabase
          .from("artworks_secure")
          .select("*")
          .in("collection_id", artworkCollectionIds);
        artworks = data || [];
      }

      // Succession : flag succession_plan_active posé par SuccessionPlanningPage
      // Mécénat : flag philanthropy_active posé par FamilyDetail
      // Les deux sont dans profiles déjà chargé via select("*")
      const clientsWithSuccessionFlag = new Set(
        (profiles || [])
          .filter((p) => p.succession_plan_active === true)
          .map((p) => p.id)
      );
      const clientsWithPhilanthropyFlag = new Set(
        (profiles || [])
          .filter((p) => p.philanthropy_active === true)
          .map((p) => p.id)
      );

      setSuccessionPlans([...clientsWithSuccessionFlag].map((id) => ({ client_id: id })));
      setPhilanthropy([...clientsWithPhilanthropyFlag].map((id) => ({ client_id: id })));

      const clientsList = (profiles || []).map((profile) => {
        const clientCollections = (collections || []).filter(
          (c) => c.client_id === profile.id
        );
        const clientCollectionIds = clientCollections.map((c) => c.id);
        const clientArtworks = (artworks || []).filter((a) =>
          clientCollectionIds.includes(a.collection_id)
        );

        let totalValue = 0;
        let purchaseValue = 0;
        clientArtworks.forEach((art) => {
          const currentValue = parseFloat(art.current_estimated_value) || parseFloat(art.valuation_estimated_value) || parseFloat(art.estimated_value) || parseFloat(art.value) || 0;
          totalValue += currentValue;
          purchaseValue += parseFloat(art.acquisition_price_eur) || parseFloat(art.acquisition_price) || parseFloat(art.price) || 0;
        });

        const growthRate =
          purchaseValue > 0 ? ((totalValue - purchaseValue) / purchaseValue) * 100 : 0;
        const riskLevel =
          totalValue > 1000000
            ? "high"
            : totalValue > 500000
            ? "medium"
            : "low";

        const hasSuccession = clientsWithSuccessionFlag.has(profile.id);
        const hasPhilanthropy = clientsWithPhilanthropyFlag.has(profile.id);

        return {
          id: profile.id,
          name: `${profile.first_name || ""} ${profile.last_name || ""}`.trim(),
          email: profile.email,
          totalValue,
          growthRate,
          riskLevel,
          collectionCount: clientCollections.length,
          artworkCount: clientArtworks.length,
          successionPlan: hasSuccession,
          philanthropy: hasPhilanthropy,
        };
      });

      setClients(clientsList);
      setCollections(collections);
      setArtworks(artworks);
      setLoading(false);
    } catch (err) {
      console.error("❌ Erreur reloadData:", err);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      reloadData();
    }
  }, [user, reloadData]);

  const [exporting, setExporting] = useState(false);

  // Récupère le profil du Family Office (branding des exports) une seule fois au clic
  const fetchFoProfile = useCallback(async () => {
    if (!user?.id) return {};
    const { data } = await supabase
      .from("profiles")
      .select("first_name, last_name, company")
      .eq("id", user.id)
      .maybeSingle();
    return data || {};
  }, [user?.id]);

  const handleExportCsv = useCallback(async () => {
    if (!artworks.length) {
      setErrorMessage(c.noExport);
      setShowErrorModal(true);
      return;
    }
    setExporting(true);
    try {
      const foProfile = await fetchFoProfile();
      exportConsolidatedCsv({ foProfile, families, clientFamilyMap, artworks });
    } catch (e) {
      console.error("Export CSV consolidé:", e);
      setErrorMessage(c.csvError);
      setShowErrorModal(true);
    } finally {
      setExporting(false);
    }
  }, [artworks, families, clientFamilyMap, fetchFoProfile, c]);

  const handleExportPdf = useCallback(async () => {
    if (!artworks.length) {
      setErrorMessage(c.noExport);
      setShowErrorModal(true);
      return;
    }
    setExporting(true);
    try {
      const foProfile = await fetchFoProfile();
      generateConsolidatedPdf({ foProfile, families, clientFamilyMap, artworks });
    } catch (e) {
      console.error("Rapport PDF consolidé:", e);
      setErrorMessage(c.pdfError);
      setShowErrorModal(true);
    } finally {
      setExporting(false);
    }
  }, [artworks, families, clientFamilyMap, fetchFoProfile, c]);

  const stats = useMemo(() => {
    const totalValue = clients.reduce((sum, c) => sum + (c.totalValue || 0), 0);
    const totalArtworks = clients.reduce((sum, c) => sum + (c.artworkCount || 0), 0);
    const avgGrowth =
      clients.length > 0
        ? clients.reduce((sum, c) => sum + (c.growthRate || 0), 0) / clients.length
        : 0;

    const clientsWithSuccession = clients.filter((c) => c.successionPlan).length;
    const clientsWithPhilanthropy = clients.filter((c) => c.philanthropy).length;

    const highRisk = clients.filter((c) => c.riskLevel === "high").length;
    const mediumRisk = clients.filter((c) => c.riskLevel === "medium").length;

    return {
      main: {
        totalClients: clients.length,
        totalValue,
        totalArtworks,
        avgGrowth,
        clientsWithSuccession,
        clientsWithPhilanthropy,
      },
      risk: {
        high: highRisk,
        medium: mediumRisk,
        low: clients.length - highRisk - mediumRisk,
      },
    };
  }, [clients]);


  // ── Vision IA stats — calculées depuis artworks déjà chargés (select("*"))
  // Les colonnes IA sont écrites par vision.artkiv.com dans la même table artworks
  const visionStats = useMemo(() => {
    if (!artworks || artworks.length === 0) return null;

    const analysed = artworks.filter((a) => a.style_ai || a.visual_analysis || a.valuation_estimated_value);
    const withConservation = artworks.filter((a) => a.condition_report && a.condition_report.trim() !== "");
    const withValuation = artworks.filter((a) => a.valuation_estimated_value || a.valuation_consensus);
    const withBlockchain = artworks.filter((a) => a.certificate_blockchain === true);

    const atRiskConservation = artworks.filter(
      (a) => a.condition_report && !a.last_valuation_date
    );

    const styleMap = {};
    artworks.forEach((a) => {
      if (a.style_ai) {
        styleMap[a.style_ai] = (styleMap[a.style_ai] || 0) + 1;
      }
    });
    const topStyles = Object.entries(styleMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([style, count]) => ({ style, count }));

    const visionTotalValue = withValuation.reduce((sum, a) => {
      return sum + (parseFloat(a.valuation_consensus) || parseFloat(a.valuation_estimated_value) || 0);
    }, 0);

    const avgConfidence = withValuation.length > 0
      ? withValuation.reduce((sum, a) => sum + (parseFloat(a.valuation_confidence_score) || 0), 0) / withValuation.length
      : 0;

    return {
      total: artworks.length,
      analysed: analysed.length,
      analysedPct: artworks.length > 0 ? Math.round((analysed.length / artworks.length) * 100) : 0,
      withConservation: withConservation.length,
      withValuation: withValuation.length,
      withBlockchain: withBlockchain.length,
      atRiskConservation: atRiskConservation.length,
      topStyles,
      visionTotalValue,
      avgConfidence: Math.round(avgConfidence * 100) / 100,
    };
  }, [artworks]);

  const filteredClients = useMemo(() => {
    let result = [...clients];

    if (searchQuery) {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterRisk !== "all") {
      result = result.filter((c) => c.riskLevel === filterRisk);
    }

    if (onlySuccession) {
      result = result.filter((c) => c.successionPlan);
    }

    if (onlyPhilanthropy) {
      result = result.filter((c) => c.philanthropy);
    }

    if (sortBy === "value") {
      result.sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
    } else if (sortBy === "risk") {
      const riskOrder = { high: 1, medium: 2, low: 3 };
      result.sort((a, b) => riskOrder[a.riskLevel] - riskOrder[b.riskLevel]);
    }

    return result;
  }, [clients, searchQuery, sortBy, filterRisk, onlySuccession, onlyPhilanthropy]);

  const goToFamily = (client) => {
    const familyId = clientFamilyMap[client.id];
    if (familyId) {
      navigate(`/family-office/families/${familyId}`);
    } else {
      setErrorMessage(
        c.noFamily(client.name)
      );
      setShowErrorModal(true);
    }
  };

  const openArtworksForClient = (client) => {
    const clientCollections = collections.filter((c) => c.client_id === client.id);
    const clientCollectionIds = clientCollections.map((c) => c.id);
    const clientArtworks = artworks.filter((a) =>
      clientCollectionIds.includes(a.collection_id)
    );
    setModalArtworks(clientArtworks);
    setShowArtworksModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#1A1A1A]" size={32} />
          <p className="text-sm text-[#666]">{c.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <DashboardHeader />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                {c.familiesFollowed}
              </p>
              <Users size={16} className="text-[#666]" />
            </div>
            <p className="text-2xl font-light text-[#1A1A1A] mb-1">
              {families.length}
            </p>
            <p className="text-xs text-[#666]">
              {c.nClients(stats.main.totalClients)}
            </p>
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                {c.totalValue}
              </p>
              <BarChart3 size={16} className="text-[#666]" />
            </div>
            <p className="text-2xl font-light text-[#1A1A1A] mb-1">
              {stats.main.totalValue
                ? new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: "EUR",
                    notation: "compact",
                  }).format(stats.main.totalValue)
                : "—"}
            </p>
            <p className="text-xs text-[#666]">{c.nWorks(stats.main.totalArtworks)}</p>
            <ValuationCaption />
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                {c.avgPerf}
              </p>
              {stats.main.avgGrowth > 0 ? (
                <ArrowUpRight size={16} className="text-emerald-600" />
              ) : (
                <ArrowDownRight size={16} className="text-red-600" />
              )}
            </div>
            <p
              className={`text-2xl font-light mb-1 ${
                stats.main.avgGrowth > 0 ? "text-emerald-600" : "text-red-600"
              }`}
            >
              {stats.main.avgGrowth > 0 ? "+" : ""}
              {stats.main.avgGrowth.toFixed(1)}%
            </p>
            <p className="text-xs text-[#666]">{c.vsAcq}</p>
          </div>

          <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                {c.riskLevel}
              </p>
              <AlertCircle size={16} className="text-[#666]" />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-xs text-[#666]">{stats.risk.high}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs text-[#666]">{stats.risk.medium}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs text-[#666]">{stats.risk.low}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white border border-[#E8E8E8] rounded p-4 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  id="succession-filter"
                  checked={onlySuccession}
                  onChange={(e) => setOnlySuccession(e.target.checked)}
                  className="w-3 h-3"
                />
                <label htmlFor="succession-filter" className="text-[#666]">
                  {c.fSucc}
                </label>
              </div>
              <div className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  id="philanthropy-filter"
                  checked={onlyPhilanthropy}
                  onChange={(e) => setOnlyPhilanthropy(e.target.checked)}
                  className="w-3 h-3"
                />
                <label htmlFor="philanthropy-filter" className="text-[#666]">
                  {c.fPatr}
                </label>
              </div>
              <select
                value={filterRisk}
                onChange={(e) => setFilterRisk(e.target.value)}
                className="px-2 py-1 text-xs border border-[#E8E8E8] rounded"
              >
                <option value="all">{c.allRisk}</option>
                <option value="high">{c.riskHigh}</option>
                <option value="medium">{c.riskMed}</option>
                <option value="low">{c.riskLow}</option>
              </select>
            </div>

            <div className="relative flex-shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-[#999]" size={14} />
              <input
                type="text"
                placeholder={c.searchClient}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-2 text-xs border border-[#E8E8E8] rounded w-full sm:w-64"
              />
            </div>

            {/* Exports consolidés multi-familles */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={exporting}
                title={c.csvTitle}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-[#E8E8E8] rounded hover:bg-[#FAFAF8] disabled:opacity-50"
              >
                <FileText size={14} /> Export CSV
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={exporting}
                title={c.pdfTitle}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[#1A1A1A] text-white rounded hover:bg-[#333] disabled:opacity-50"
              >
                <FileText size={14} /> {exporting ? c.generating : c.pdfReport}
              </button>
            </div>
          </div>
        </div>

        {/* POINTS À TRAITER CE TRIMESTRE - DÉPLACÉ ICI */}
        <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 shadow-sm mb-10">
          <div className="mb-6">
            <h2 className="text-lg font-light text-[#1A1A1A] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              {c.todoTitle}
            </h2>
            <div className="h-0.5 w-16 bg-[#C9A961] rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.main.totalClients - stats.main.clientsWithSuccession > 0 && (
              <div className="group">
                <div className="flex flex-col h-full">
                  <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                    1
                  </div>
                  <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                    {c.t1}
                  </h4>
                  <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                    {c.noPlan(stats.main.totalClients - stats.main.clientsWithSuccession)}
                  </p>
                  <Link
                    to="/estimation-succession"
                    className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                  >
                    {c.t1link}
                  </Link>
                </div>
              </div>
            )}

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  2
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  {c.t2}
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  {c.t2txt}
                </p>
                <Link
                  to="/family-office/market-valuations"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  {c.t2link}
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  3
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  {c.t3}
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  {c.t3txt}
                </p>
                <Link
                  to="/family-office/philanthropie"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  {c.t3link}
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  3
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  {c.t4}
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  {c.t4txt}
                </p>
                <Link
                  to="/family-office/en-developpement?module=museum-loans"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  {c.t4link}
                </Link>
              </div>
            </div>

            <div className="group">
              <div className="flex flex-col h-full">
                <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-white font-medium text-base shadow-md mb-3">
                  4
                </div>
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">
                  {c.t5}
                </h4>
                <p className="text-xs text-[#666] mb-3 leading-relaxed flex-1">
                  {c.t5txt}
                </p>
                <Link
                  to="/family-office/en-developpement?module=auction-houses"
                  className="inline-flex items-center gap-1.5 text-xs text-[#1A1A1A] hover:text-[#666] font-medium group-hover:gap-2 transition-all"
                >
                  {c.t5link}
                </Link>
              </div>
            </div>
          </div>
        </div>


        {/* ── Vision IA — Intelligence collection ── */}
        {visionStats && (
          <div className="bg-white border border-[#E8E8E8] rounded-xl p-6 shadow-sm mb-10">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs font-medium text-[#C9A961] tracking-widest uppercase mb-1">
                  {c.visionBrand}
                </p>
                <h2 className="text-lg font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                  {c.visionTitle}
                </h2>
              </div>
              <a
                href={VISION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#C9A961] border border-[#C9A961] px-3 py-1.5 rounded hover:bg-[#C9A961] hover:text-white transition-colors"
              >
                {c.visionOpen}
              </a>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">{c.aiAnalyses}</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.analysedPct}%</p>
                <p className="text-xs text-[#666]">{visionStats.analysed} / {visionStats.total} {c.worksLower}</p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">{c.consReports}</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withConservation}</p>
                <p className="text-xs text-[#666]">
                  {visionStats.atRiskConservation > 0 ? (
                    <span className="text-amber-600">{visionStats.atRiskConservation} {c.toRevisit}</span>
                  ) : (
                    <span className="text-emerald-600">{c.allUpToDate}</span>
                  )}
                </p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">{c.aiValuations}</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withValuation}</p>
                <p className="text-xs text-[#666]">
                  {visionStats.visionTotalValue > 0
                    ? new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", notation: "compact" }).format(visionStats.visionTotalValue)
                    : "—"}
                  {visionStats.avgConfidence > 0 && (
                    <span className="text-[#999] ml-1">· {c.conf} {visionStats.avgConfidence}</span>
                  )}
                </p>
              </div>
              <div className="bg-[#F9F7F4] rounded-lg p-4">
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-1">{c.blockchain}</p>
                <p className="text-2xl font-light text-[#1A1A1A]">{visionStats.withBlockchain}</p>
                <p className="text-xs text-[#666]">{c.certsIssued}</p>
              </div>
            </div>

            {/* Styles dominants */}
            {visionStats.topStyles.length > 0 && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wide mb-3">{c.topStyles}</p>
                <div className="flex flex-wrap gap-2">
                  {visionStats.topStyles.map(({ style, count }) => (
                    <span
                      key={style}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F9F7F4] border border-[#E8E0D0] rounded-full text-xs text-[#444]"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] inline-block" />
                      {style}
                      <span className="text-[#999]">({count})</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Alerte si peu d'oeuvres analysées */}
            {visionStats.analysedPct < 50 && visionStats.total > 0 && (
              <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  {c.visionWarn1}{" "}
                  <a href={VISION_URL} target="_blank" rel="noopener noreferrer" className="underline">
                    {c.visionWarnLink}
                  </a>{" "}
                  {c.visionWarn2}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Portfolio Readiness (§9.3) — préparation patrimoniale par famille, moteur commun */}
        <div className="mb-10">
          <div className="mb-3 flex justify-end">
            <FamilyOfficeReportButton
              clients={clients}
              foName={`${profile?.first_name || ""} ${profile?.last_name || ""}`.trim() || profile?.company || "Family Office"}
            />
          </div>
          <PortfolioReadinessCard clients={clients} />
        </div>

        {/* Vue liste des clients + colonne droite */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                  {c.portfolio}
                </p>
                <h3 className="text-lg font-light text-[#1A1A1A]">
                  {c.followedClients(filteredClients.length)}
                </h3>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={() => setSortBy("value")}
                  className={`px-2 py-1 rounded border ${
                    sortBy === "value"
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white border-[#E8E8E8] text-[#666]"
                  }`}
                >
                  {c.byValue}
                </button>
                <button
                  onClick={() => setSortBy("risk")}
                  className={`px-2 py-1 rounded border ${
                    sortBy === "risk"
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white border-[#E8E8E8] text-[#666]"
                  }`}
                >
                  {c.byRisk}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="bg-white border border-[#E8E8E8] rounded p-4 hover:border-[#1A1A1A] hover:shadow-sm transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">
                        {client.name}
                      </p>
                      {client.successionPlan && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100">
                          {c.badgeSucc}
                        </span>
                      )}
                      {client.philanthropy && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {c.badgeMec}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#666] mb-1 truncate">
                      {client.email || c.noContact}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#999]">
                      <span className="flex items-center gap-1">
                        <FolderOpen size={11} />
                        <span>
                          {c.nCollections(client.collectionCount || 0)}
                        </span>
                      </span>
                      <span>•</span>
                      <span>
                        {c.nArtworks(client.artworkCount || 0)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 min-w-[170px]">
                    <div className="text-right">
                      <p className="text-xs text-[#999]">{c.estValue}</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {client.totalValue
                          ? new Intl.NumberFormat(locale, {
                              style: "currency",
                              currency: "EUR",
                              notation: "compact",
                            }).format(client.totalValue)
                          : "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      {client.growthRate > 0 ? (
                        <>
                          <ArrowUpRight size={12} className="text-emerald-600" />
                          <span className="text-emerald-600">
                            +{client.growthRate.toFixed(1)}%
                          </span>
                        </>
                      ) : client.growthRate < 0 ? (
                        <>
                          <ArrowDownRight size={12} className="text-red-600" />
                          <span className="text-red-600">
                            {client.growthRate.toFixed(1)}%
                          </span>
                        </>
                      ) : (
                        <span className="text-[#999]">0,0%</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => goToFamily(client)}
                        className="text-xs text-[#1A1A1A] flex items-center gap-1 hover:underline"
                      >
                        {c.viewFamily}
                        <ChevronRight size={12} />
                      </button>
                      <button
                        onClick={() => openArtworksForClient(client)}
                        className="text-xs text-[#666] hover:text-[#1A1A1A] underline hover:no-underline"
                        title={c.managedTitle}
                      >
                        {c.managedArt}
                      </button>
                      <button
                        onClick={() => navigate(`/succession?clientId=${client.id}`)}
                        className="text-xs text-[#666] hover:text-[#1A1A1A] underline hover:no-underline"
                        title={c.transTitle}
                      >
                        {c.transmissionPlan}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Colonne droite : modules experts + actions structurantes fusionnés */}
          <div className="space-y-6">

            {/* ── RAPPORTS ── */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs font-medium text-[#C9A961] tracking-widest uppercase mb-1">ARTICHAUT</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                    {c.reportsTitle}
                  </h3>
                </div>
                <FileText size={18} className="text-[#C9A961]" />
              </div>
              <p className="text-xs text-[#666] mb-4 leading-relaxed">
                {c.reportsIntro}
              </p>
              <div className="space-y-2">
                {[
                  { type: "overview",     label: c.repOverview,  sub: c.repOverviewSub },
                  { type: "succession",   label: c.repSucc,  sub: c.repSuccSub },
                  { type: "philanthropy", label: c.repPhil, sub: c.repPhilSub },
                ].map(({ type, label, sub }) => (
                  <button
                    key={type}
                    onClick={() => { setReportModalType(type); setShowReportModal(true); }}
                    className="w-full text-left border border-[#E8E8E8] rounded-lg px-4 py-3 hover:border-[#C9A961] hover:bg-[#F9F7F4] transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">{label}</p>
                        <p className="text-xs text-[#999] mt-0.5">{sub}</p>
                      </div>
                      <Download size={14} className="text-[#CCC] group-hover:text-[#C9A961] transition-colors flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#BBB] mt-3">
                {c.reportsNote}
              </p>
            </div>

            {/* MODULES EXPERTS & ACTIONS STRUCTURANTES */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-[#F9F7F4] border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                  <Target className="text-[#C9A961]" size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-medium text-[#C9A961] tracking-widest uppercase mb-1">ARTICHAUT</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                    {c.modulesTitle}
                  </h3>
                  <p className="text-xs text-[#999] mt-0.5">
                    {c.modulesSub}
                  </p>
                </div>
              </div>

              {/* Actions cliquables vers modules */}
              <div className="space-y-2 mb-5">
                <button
                  onClick={() => setActiveModule("succession")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <Scale className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          {c.m1}
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        {c.withPlans(stats.main.clientsWithSuccession)}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveModule("insurance")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          {c.m2}
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        {c.m2txt}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setActiveModule("philanthropy")}
                  className="w-full bg-[#F9F7F4] border border-[#E8E0D0] rounded-lg p-4 hover:border-[#C9A961] hover:bg-[#FBF9F5] transition-all group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                      <HeartHandshake className="text-[#C9A961]" size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-medium text-[#1A1A1A] group-hover:text-[#C9A961] transition-colors">
                          {c.m3}
                        </h4>
                        <ChevronRight className="text-[#CCC] group-hover:text-[#C9A961] transition-colors" size={14} />
                      </div>
                      <p className="text-xs text-[#999] leading-relaxed">
                        {c.m3txt}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Séparateur */}
              <div className="h-px bg-[#E8E8E8] my-4"></div>

              {/* Actions complémentaires */}
              <div className="space-y-1.5">
                <p className="text-[10px] text-[#BBB] uppercase tracking-widest mb-3">{c.priorities}</p>

                <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#F9F7F4] hover:bg-[#F5F0E8] transition-colors">
                  <Landmark className="text-[#C9A961] flex-shrink-0" size={13} />
                  <span className="text-xs text-[#555]">{c.p1}</span>
                </div>

                <div className="flex items-center gap-3 px-3 py-2 rounded bg-[#F9F7F4] hover:bg-[#F5F0E8] transition-colors">
                  <Building2 className="text-[#C9A961] flex-shrink-0" size={13} />
                  <span className="text-xs text-[#555]">{c.p2}</span>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Section modules détaillés */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale module actif */}
          <div className="lg:col-span-2 space-y-4">
            {activeModule === "succession" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      {c.succMod}
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      {c.succModSub}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-[#666]">
                    <FileText size={14} />
                    <span>
                      {c.withFile(stats.main.clientsWithSuccession)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  {c.succModTxt}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.scen}</p>
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {c.scenTxt}
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.params}</p>
                    <p className="text-sm text-[#666]">
                      {c.paramsTxt}
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.outFmt}</p>
                    <p className="text-sm text-[#666]">
                      {c.outFmtTxt}
                    </p>
                  </div>
                </div>

                {/* Focus planification fiscale -> ComingSoon */}
                <div className="border border-[#E8E8E8] rounded-lg p-3 bg-[#FCFCFB]">
                  <p className="text-[11px] text-[#999] mb-1">
                    {c.taxPlan}
                  </p>
                  <p className="text-xs text-[#666] mb-2">
                    {c.taxPlanTxt}
                  </p>
                  <Link
                    to="/family-office/en-developpement?module=fiscal-optimization"
                    className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-[#1A1A1A] transition-colors"
                  >
                    {c.inDev}
                  </Link>
                </div>
              </div>
            )}

            {activeModule === "insurance" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      {c.insMod}
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      {c.insModSub}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  {c.insModTxt}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.cover}</p>
                    <p className="text-sm text-[#1A1A1A]">
                      {c.coverTxt}
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.exclus}</p>
                    <p className="text-sm text-[#666]">
                      {c.exclusTxt}
                    </p>
                  </div>
                  <div className="border border-[#E8E8E8] rounded-lg p-3">
                    <p className="text-[11px] text-[#999] mb-1">{c.alerts}</p>
                    <p className="text-sm text-[#666]">
                      {c.alertsTxt}
                    </p>
                  </div>
                </div>

                <div className="border border-[#E8E8E8] rounded-lg p-3 bg-[#FAFAF8]">
                  <p className="text-[11px] text-[#999] mb-1">
                    {c.insFull}
                  </p>
                  <p className="text-xs text-[#666] mb-2">
                    {c.insFullTxt}
                  </p>
                  <Link
                    to="/family-office/en-developpement?module=insurance"
                    className="inline-flex items-center gap-1 text-[11px] text-[#C9A961] hover:text-[#1A1A1A] transition-colors"
                  >
                    {c.inDev}
                  </Link>
                </div>
              </div>
            )}

            {activeModule === "philanthropy" && (
              <div className="bg-white border border-[#E8E8E8] rounded p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs font-medium text-[#999] tracking-wide uppercase">
                      {c.phMod}
                    </p>
                    <h3 className="text-lg font-light text-[#1A1A1A]">
                      {c.phModSub}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-[#666] mb-4">
                  {c.phModTxt}
                </p>

                {/* 7 actions de mécénat culturel */}
                <h4 className="text-sm font-medium text-[#1A1A1A] mb-1">
                  {c.phSeven}
                </h4>
                <p className="text-xs text-[#666] mb-4">
                  {c.phSevenTxt}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 1. Prêts aux institutions */}
                  <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Landmark className="w-4 h-4 text-blue-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a1}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a1t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a1b}
                    </p>
                  </div>

                  {/* 2. Expositions publiques */}
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <PieChart className="w-4 h-4 text-amber-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a2}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a2t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a2b}
                    </p>
                  </div>

                  {/* 3. Conservation & restauration */}
                  <div className="border border-teal-200 bg-teal-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-teal-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a3}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a3t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a3b}
                    </p>
                  </div>

                  {/* 4. Location aux entreprises */}
                  <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="w-4 h-4 text-emerald-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a4}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a4t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a4b}
                    </p>
                  </div>

                  {/* 5. Donation avec usufruit */}
                  <div className="border border-purple-200 bg-purple-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a5}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a5t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a5b}
                    </p>
                  </div>

                  {/* 6. Actions éducatives */}
                  <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-indigo-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a6}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a6t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a6b}
                    </p>
                  </div>

                  {/* 7. Achat pour donation */}
                  <div className="border border-rose-200 bg-rose-50 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-1">
                      <Heart className="w-4 h-4 text-rose-600" />
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {c.a7}
                      </p>
                    </div>
                    <p className="text-xs text-[#666] mb-1">
                      {c.a7t}
                    </p>
                    <p className="text-[11px] text-emerald-700 font-medium">
                      {c.a7b}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ========== COLONNE DROITE - PRIORISATION ========== */}
          <div className="space-y-6">
            {/* PRIORISATION */}
            <div className="bg-white border border-[#E8E8E8] rounded-xl shadow-sm p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F9F7F4] border border-[#E8E0D0] flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="text-[#C9A961]" size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] font-medium text-[#C9A961] tracking-widest uppercase mb-1">ARTICHAUT</p>
                  <h3 className="text-base font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                    {c.prio}
                  </h3>
                  <p className="text-xs text-[#999] mt-1 leading-relaxed">
                    {c.prioTxt}
                  </p>
                </div>
              </div>
              <button className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white rounded px-4 py-3 text-sm font-medium transition-all flex items-center justify-between group">
                <span>{c.quarterly}</span>
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showArtworksModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light text-[#1A1A1A]" style={{ fontFamily: 'Georgia, serif' }}>
                {c.managedArt}
              </h2>
              <button
                onClick={() => setShowArtworksModal(false)}
                className="text-[#666] hover:text-[#1A1A1A] text-2xl leading-none"
              >
                ✕
              </button>
            </div>
            
            {modalArtworks.length === 0 ? (
              <p className="text-sm text-[#666] text-center py-8">
                {c.mNone}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[#999] border-b border-[#E8E8E8]">
                    <tr>
                      <th className="pb-3 pr-4 font-medium">{c.mTitle}</th>
                      <th className="pb-3 pr-4 font-medium">{c.mArtist}</th>
                      <th className="pb-3 pr-4 font-medium">{c.mValue}</th>
                      <th className="pb-3 pr-4 font-medium">{c.mLoc}</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalArtworks.map((art) => (
                      <tr key={art.id} className="border-b border-[#E8E8E8] hover:bg-[#FAFAFA] transition-colors">
                        <td className="py-3 pr-4">
                          <span className="text-[#1A1A1A] font-medium">
                            {art.title || c.untitled}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-[#666]">
                          {art.artist || c.unknownArtist}
                        </td>
                        <td className="py-3 pr-4 text-[#1A1A1A]">
                          {art.estimated_value 
                            ? new Intl.NumberFormat(locale, {
                                style: "currency",
                                currency: "EUR",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }).format(art.estimated_value)
                            : "—"}
                        </td>
                        <td className="py-3 pr-4 text-[#666]">
                          {art.location || "—"}
                        </td>
                        <td className="py-3 flex flex-col gap-1">
  <button
    onClick={() => openSalePreparation(art)}
    className="text-xs text-blue-600 underline hover:no-underline font-medium"
  >
    {c.sellPrep}
  </button>
</td>

                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-lg font-medium text-[#1A1A1A] mb-2">{c.errTitle}</h2>
            <p className="text-sm text-[#666] mb-4">{errorMessage}</p>
            <button
              onClick={() => setShowErrorModal(false)}
              className="w-full bg-[#1A1A1A] text-white rounded py-2 text-sm hover:bg-[#333]"
            >
              {c.close}
            </button>
          </div>
        </div>
      )}

      {showSalePrep && selectedArtwork && (
        <SalePreparation
          artwork={selectedArtwork}
          onClose={() => {
            setShowSalePrep(false);
            setSelectedArtwork(null);
          }}
        />
      )}
      {/* ── Modal sélection famille pour rapport ── */}
      {showReportModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowReportModal(false)}
        >
          <div
            className="bg-white rounded-xl max-w-md w-full shadow-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="text-xs text-[#C9A961] uppercase tracking-widest mb-1">ARTICHAUT</p>
                <h3 className="text-lg font-light text-[#1A1A1A]" style={{ fontFamily: "Georgia, serif" }}>
                  {{ overview: c.repOverview, succession: c.repSucc, philanthropy: c.repPhil }[reportModalType]}
                </h3>
              </div>
              <button onClick={() => setShowReportModal(false)} className="text-[#999] hover:text-[#1A1A1A] text-xl leading-none">✕</button>
            </div>
            <p className="text-sm text-[#666] mb-5">
              {c.repPick}
            </p>
            {families.length === 0 ? (
              <p className="text-sm text-[#999] text-center py-4">{c.noFam}</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {families.map((fam) => (
                  <button
                    key={fam.id}
                    onClick={() => { setShowReportModal(false); navigate(`/family-office/families/${fam.id}?rapport=${reportModalType}`); }}
                    className="w-full text-left border border-[#E8E8E8] rounded-lg px-4 py-3 hover:border-[#C9A961] hover:bg-[#F9F7F4] transition-all"
                  >
                    <p className="text-sm font-medium text-[#1A1A1A]">{fam.label || c.familyFallback}</p>
                    <p className="text-xs text-[#999] mt-0.5">
                      {c.nMembers(fam.clientCount || 0)}
                      {fam.client_name ? ` · ${fam.client_name}` : ""}
                    </p>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => setShowReportModal(false)} className="mt-4 w-full text-sm text-[#666] hover:text-[#1A1A1A]">
              {c.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}